/**
 * Module canonique de validation et normalisation des types de relation
 * 
 * RÈGLE CRITIQUE : Ce fichier doit rester identique à supabase/functions/_shared/relationValidation.ts
 * Toute modification doit être répliquée dans les deux fichiers pour garantir la parité frontend/edge.
 * 
 * @module relationValidation
 */

/**
 * Type de relation normalisé (valeurs canoniques)
 */
export type NormalizedRelationType = 'manager' | 'collègue' | 'client' | 'autre' | '';

/**
 * Statut du check de relation
 */
export type RelationCheckStatus = 'conforme' | 'non-conforme' | 'non-communique';

/**
 * Résultat du check de relation
 */
export interface RelationCheckResult {
  status: RelationCheckStatus;
  message: string;
  isValid: boolean;
}

/**
 * Normalise un type de relation pour comparaison stricte
 * 
 * Mapping strict :
 * - "Manager direct" / "manager direct" → "manager"
 * - "manager" → "manager"
 * - "collègue" / "Collègue" → "collègue"
 * - "client" / "Client" → "client"
 * - "autre" → "autre"
 * - Valeur inconnue (ex: IA "RH") → "autre"
 * - Valeur manquante (null/undefined) → "" (pour détecter "manquant")
 * 
 * @param value - Valeur brute depuis DB ou formulaire
 * @returns Valeur normalisée
 */
export function normalizeRelationType(value: string | null | undefined): NormalizedRelationType {
  // Cas 1 : Valeur manquante
  if (!value || value.trim() === '') {
    return '';
  }
  
  // Cas 2 : Normalisation (lowercase + trim)
  const normalized = value.toLowerCase().trim();
  
  // Cas 3 : Manager (toutes variantes)
  if (normalized.includes('manager') || normalized === 'n+1' || normalized === 'superviseur') {
    return 'manager';
  }
  
  // Cas 4 : Collègue (avec ou sans accent)
  if (normalized.includes('collègue') || normalized.includes('collegue') || normalized.includes('colleague')) {
    return 'collègue';
  }
  
  // Cas 5 : Client
  if (normalized.includes('client')) {
    return 'client';
  }
  
  // Cas 6 : Autre (explicite)
  if (normalized === 'autre' || normalized === 'other') {
    return 'autre';
  }
  
  // Cas 7 : Valeur inconnue → autre
  return 'autre';
}

/**
 * Calcule le check de conformité relation pour un référent
 * 
 * Logique :
 * 1. Si une des valeurs est manquante → non-conforme "données manquantes"
 * 2. Sinon, normalisation + comparaison stricte
 * 3. Si identiques après normalisation → conforme
 * 4. Sinon → non-conforme avec détail de l'incohérence
 * 
 * @param expected - Type attendu par le candidat (referents.relation_type)
 * @param confirmed - Type confirmé par le référent (referent_responses.relation_type_confirmed)
 * @returns Check avec statut, message et flag de validité
 */
export function computeRelationCheck(
  expected: string | null | undefined,
  confirmed: string | null | undefined
): RelationCheckResult {
  // Cas 1 : Données manquantes
  if (!expected || !confirmed || expected.trim() === '' || confirmed.trim() === '') {
    return {
      status: 'non-communique',
      message: 'Données de relation manquantes',
      isValid: false
    };
  }
  
  // Cas 2 : Normalisation et comparaison
  const normalizedExpected = normalizeRelationType(expected);
  const normalizedConfirmed = normalizeRelationType(confirmed);
  
  // Cas 3 : Conforme
  if (normalizedExpected === normalizedConfirmed) {
    return {
      status: 'conforme',
      message: `Niveau de relation confirmé : ${normalizedConfirmed}`,
      isValid: true
    };
  }
  
  // Cas 4 : Non conforme
  return {
    status: 'non-conforme',
    message: `Incohérence : attendu "${normalizedExpected}", confirmé "${normalizedConfirmed}"`,
    isValid: false
  };
}
